import React from 'react'
import { isMobile } from 'react-device-detect'
import {
  faCheck,
  faXmark,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons'
import {
  PixelIcon,
  PixelTable,
  PixelText,
  PixelCurrency,
  colors,
} from 'pixel-base'
import styled from 'styled-components'

interface DispatchDetailsTableProps {
  data: any[]
  loading: boolean
  onSelectedRowsChange: (row: any) => void
  clearSelectedRows: boolean
  isSelectable: boolean
  filterText?: string
  onRowClick: (row: any) => void
}

const HintContainer = styled.div`
  width: 100%;
  padding: 8px 16px;
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
  font-size: 0.875rem;
  color: #6c757d;
`

const ProcessingTimeContainer = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  color: ${(props) => props.color};
`

const ExpandedComponent: React.FC<{ data: any }> = ({ data }) => {
  if (!data?.customer_notes) return null
  return (
    <HintContainer
      data-testid="expanded-row-content"
      role="region"
      aria-label="Customer Notes"
    >
      <strong>Customer Notes:</strong> {data.customer_notes}
    </HintContainer>
  )
}

const DispatchDetailsTable: React.FC<DispatchDetailsTableProps> = ({
  data,
  loading,
  onSelectedRowsChange,
  clearSelectedRows,
  isSelectable,
  filterText = '',
  onRowClick,
}) => {
  const customFilter = (val: string, data: any[]) => {
    return data.filter((e) =>
      Object.values(e).some((v) => String(v).includes(val)),
    )
  }

  const getProcessingTimeColor = (days: number) => {
    if (days <= 3) return 'rgb(40, 167, 69)' // green
    if (days <= 5) return 'rgb(255, 193, 7)' // yellow
    return 'rgb(220, 53, 69)' // red
  }

  const columns = [
    {
      dataKey: 'tracking_id',
      name: 'Tracking',
      sortable: true,
      width: '14%',
      selector: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <PixelText copyToClipboard>{row.tracking_id}</PixelText>
          {row.customer_notes && (
            <PixelIcon icon={faInfoCircle} color={colors.primary} />
          )}
        </div>
      ),
    },
    {
      dataKey: 'order_id',
      name: 'Orders',
      sortable: true,
      width: '15%',
      selector: (row) => row.source_order_id,
    },
    {
      dataKey: 'first_name',
      name: 'Customer',
      sortable: true,
      width: '14%',
      selector: (row) => row.first_name,
    },
    {
      dataKey: 'city',
      name: 'City',
      sortable: true,
      width: '14%',
      selector: (row) => row.city,
    },
    {
      dataKey: 'no_of_pieces',
      name: 'Pcs',
      sortable: true,
      selector: (row) => row.no_of_pieces,
    },
    {
      dataKey: 'weight',
      name: 'Weight(kg)',
      sortable: true,
      selector: (row) => row.weight,
    },
    {
      dataKey: 'cod_amount',
      name: 'COD Amount',
      sortable: true,
      selector: (row) => <PixelCurrency value={row.cod_amount} />,
    },
    {
      dataKey: 'processing_time',
      name: 'Process Time',
      sortable: true,
      selector: (row) => {
        const days = row.processing_time || 0
        return (
          <ProcessingTimeContainer
            color={getProcessingTimeColor(days)}
            data-testid={`processing-time-${row.id}`}
            style={{ color: getProcessingTimeColor(days) }}
          >
            {days} {days === 1 ? 'day' : 'days'}
          </ProcessingTimeContainer>
        )
      },
    },
    {
      dataKey: 'is_packed',
      name: 'Packed',
      sortable: true,
      selector: (row) => (
        <PixelIcon
          icon={row.is_packed ? faCheck : faXmark}
          color={row.is_packed ? 'green' : 'red'}
        />
      ),
    },
  ]

  const filteredData = React.useMemo(() => {
    return customFilter(filterText, data)
  }, [filterText, data])

  return (
    <PixelTable
      pagination={true}
      columns={columns}
      progressPending={loading}
      data={filteredData}
      paginationRowsPerPageOptions={[25, 50, 100, 250, 500]}
      highlightOnHover={true}
      pointerOnHover={true}
      selectableRows={isSelectable}
      onSelectedRowsChange={onSelectedRowsChange}
      clearSelectedRows={clearSelectedRows}
      expandableRows={true}
      fixedHeader
      fixedHeaderScrollHeight={
        isMobile ? 'calc(100vh - 600px)' : 'calc(100vh - 550px)'
      }
      expandableRowsComponent={ExpandedComponent}
      expandableRowsHideExpander={true}
      expandableRowExpanded={(row) => Boolean(row.customer_notes)}
      onRowClicked={(row) => {
        onRowClick(row)
      }}
      data-testid="pixel-table"
      aria-busy={loading ? 'true' : 'false'}
      customStyles={{
        rows: {
          style: {
            '&[data-testid]': {
              cursor: 'pointer',
            },
          },
        },
        expanderButton: {
          style: {
            display: 'none',
          },
        },
      }}
    />
  )
}

export default DispatchDetailsTable
